import type {
  AlertGroup,
  ValidationResult,
  WebsitePreviewErrorCode,
} from './types';
import { parseWebsiteErrorCode } from './websiteApi';

/**
 * Classifier 입력 타입.
 *
 * - `validation`: 클라이언트 사전 검증(`validateWebsiteUrl`)이 실패한 결과를
 *   그대로 전달.
 * - `apiError`: preview/generate API가 throw한 에러. `stage`로 어느 단계에서
 *   발생했는지 구분해야 stage-specific fallback이 가능하다.
 *
 * 호출자가 이미 AlertGroup을 결정해 둔 케이스(e.g. preview 200 응답이지만
 * `isExtractable === false`)는 분류 대상이 아니라 알려진 도메인 시그널이므로
 * classifier를 거치지 않고 hook의 `for*` 메서드에서 render에 직접 위임한다.
 */
export type ClassifyInput =
  | {
      kind: 'validation';
      result: Extract<ValidationResult, { isValid: false }>;
    }
  | { kind: 'apiError'; error: unknown; stage: 'preview' | 'generate' };

/**
 * 분류 결과 — 사용자에게 보여줄 group과 logging용 reason 차원을 함께 노출한다.
 * 호출자(hook)가 동일한 분기를 재계산하지 않도록 한 곳에 모은다.
 */
export interface Classification {
  group: AlertGroup;
  reason: string;
}

const VALIDATION_TO_GROUP: Record<
  Extract<ValidationResult, { isValid: false }>['error'],
  AlertGroup
> = {
  INVALID_URL: 'invalidUrl',
  UNUSABLE_URL: 'unusableUrl',
  FILE_URL: 'fileUrl',
};

/**
 * Stage 의존 코드(`http_error` / `timeout`)는 의도적으로 제외한다 — 단계별로
 * 다른 group으로 흘러가야 하므로 stage fallback에서 처리한다. `Partial<Record>`
 * 대신 `Record<Exclude<…>>`로 좁혀, 새 코드가 추가될 때 매핑 누락이
 * 컴파일타임에 드러나도록 한다.
 */
type StageAgnosticCode = Exclude<
  WebsitePreviewErrorCode,
  'http_error' | 'timeout'
>;

const STAGE_AGNOSTIC_CODE_TO_GROUP: Record<StageAgnosticCode, AlertGroup> = {
  invalid_url: 'invalidUrl',
  invalid_scheme: 'unusableUrl',
  blocked_by_ssrf: 'unusableUrl',
  csr_detected: 'unusableWebsite',
  empty_extraction: 'unusableWebsite',
  content_too_short: 'contentTooShort',
  oversize: 'contentTooLarge',
  login_required: 'loginRequiredWebsite',
  target_not_found: 'websiteNotFound',
};

const isStageAgnostic = (
  code: WebsitePreviewErrorCode,
): code is StageAgnosticCode => code !== 'http_error' && code !== 'timeout';

/**
 * 사용자 입력 검증 결과 또는 서버 응답 에러를 사용자 관점 AlertGroup +
 * logging reason으로 매핑. 순수 함수 — side effect 없음.
 *
 * 미분류 fallback은 stage에 따라 갈리며, stage 의존 코드(`http_error` /
 * `timeout`)도 동일한 fallback을 공유한다.
 */
export function classifyWebsiteError(input: ClassifyInput): Classification {
  if (input.kind === 'validation') {
    return {
      group: VALIDATION_TO_GROUP[input.result.error],
      reason: input.result.error,
    };
  }

  const stageFallback: AlertGroup =
    input.stage === 'preview' ? 'websiteInfoUnavailable' : 'generateFailed';
  const code = parseWebsiteErrorCode(input.error);

  if (!code) {
    return { group: stageFallback, reason: 'UNKNOWN' };
  }

  return {
    group: isStageAgnostic(code)
      ? STAGE_AGNOSTIC_CODE_TO_GROUP[code]
      : stageFallback,
    reason: code,
  };
}
